/**
 * CRM Cuenta Corriente — agregación en cliente sin nuevas APIs.
 * Saldo alineado con ficha CC: resolveSaldoVenta + buildFilasCuentaCorriente + resumirCuentaCorriente.
 */
import {
  buildFilasCuentaCorriente,
  extractVentasCliente,
  normalizeClienteId,
  resumirCuentaCorriente
} from './cuentaCorriente';

export const CRM_FILTROS = {
  TODOS: 'todos',
  CON_DEUDA: 'con_deuda',
  AL_DIA: 'al_dia',
  VENCIDOS: 'vencidos',
  TOP_DEUDORES: 'top_deudores',
  SIN_MOVIMIENTOS: 'sin_movimientos'
};

export const CRM_ESTADOS = {
  AL_DIA: 'al_dia',
  CON_DEUDA: 'con_deuda',
  VENCIDO: 'vencido',
  SIN_MOVIMIENTOS: 'sin_movimientos'
};

export const CRM_FILTRO_OPCIONES = [
  { id: CRM_FILTROS.TODOS, label: 'Todos' },
  { id: CRM_FILTROS.CON_DEUDA, label: 'Con deuda' },
  { id: CRM_FILTROS.AL_DIA, label: 'Al día' },
  { id: CRM_FILTROS.VENCIDOS, label: 'Vencidos' },
  { id: CRM_FILTROS.TOP_DEUDORES, label: 'Top deudores' },
  { id: CRM_FILTROS.SIN_MOVIMIENTOS, label: 'Sin movimientos' }
];

function obtenerNombreCliente(cliente) {
  return [cliente?.nombre, cliente?.apellido].filter(Boolean).join(' ').trim()
    || cliente?.nombre_completo
    || `Cliente ${String(cliente?.id || '').slice(0, 8)}`;
}

function parseFechaMs(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

function extraerFechasVentas(ventas) {
  let ultimaCompraMs = 0;
  let ultimoPagoMs = 0;

  (ventas || []).forEach((v) => {
    const compraMs = parseFechaMs(v.fecha || v.fechaCreacion);
    if (compraMs > ultimaCompraMs) ultimaCompraMs = compraMs;

    const pagoMs = parseFechaMs(v.fecha_ultimo_pago || v.ultimo_pago);
    const pagado = parseFloat(v.total_pagado ?? v.monto_pagado ?? 0);
    const candidatoPago = pagoMs || (pagado > 0 ? compraMs : 0);
    if (candidatoPago > ultimoPagoMs) ultimoPagoMs = candidatoPago;
  });

  return {
    ultimaCompra: ultimaCompraMs ? new Date(ultimaCompraMs).toISOString() : null,
    ultimoPago: ultimoPagoMs ? new Date(ultimoPagoMs).toISOString() : null
  };
}

function resolverEstadoCliente({ saldo, montoVencido, tieneMovimientos }) {
  if (saldo > 0) {
    return montoVencido > 0 ? CRM_ESTADOS.VENCIDO : CRM_ESTADOS.CON_DEUDA;
  }
  if (tieneMovimientos) return CRM_ESTADOS.AL_DIA;
  return CRM_ESTADOS.SIN_MOVIMIENTOS;
}

function metricasDesdeFilasCc(filasCc) {
  let maxDiasAtraso = 0;
  let fechaMasAntigua = null;

  (filasCc || []).forEach((fila) => {
    if (fila.diasAtraso > maxDiasAtraso) maxDiasAtraso = fila.diasAtraso;
    const fMs = parseFechaMs(fila.fecha);
    if (fMs && (!fechaMasAntigua || fMs < parseFechaMs(fechaMasAntigua))) {
      fechaMasAntigua = fila.fecha;
    }
  });

  return { maxDiasAtraso, fechaMasAntigua };
}

/**
 * Misma cadena que CuentaCorrienteCliente: extractVentas → buildFilas → resumir.
 * No usa saldo_deudor ni saldo_pendiente crudo como monto.
 */
export function buildFilasCrmCuentaCorriente(clientes, cuentasRaw, diasCriterio) {
  return (clientes || []).map((cliente) => {
    const id = normalizeClienteId(cliente.id);
    const ventas = extractVentasCliente(cuentasRaw, id);
    const filasCc = buildFilasCuentaCorriente(ventas, diasCriterio);
    const resumen = resumirCuentaCorriente(filasCc, diasCriterio);
    const { maxDiasAtraso, fechaMasAntigua } = metricasDesdeFilasCc(filasCc);

    const saldo = resumen.totalDeuda;
    const montoVencido = resumen.montoVencido;
    const cantidadVentasDeuda = resumen.cantidadVentas;
    const { ultimaCompra, ultimoPago } = extraerFechasVentas(ventas);
    const tieneMovimientos = cantidadVentasDeuda > 0
      || Boolean(cliente.ultima_compra)
      || parseFloat(cliente.cantidad_ventas) > 0
      || parseFloat(cliente.total_ventas) > 0;

    const estado = resolverEstadoCliente({ saldo, montoVencido, tieneMovimientos });

    return {
      id: cliente.id,
      nombre: obtenerNombreCliente(cliente),
      codigo: cliente.codigo || cliente.codigo_cliente || cliente.numero || '',
      telefono: cliente.telefono || '',
      dni_cuit: cliente.dni_cuit || '',
      documento: cliente.documento || cliente.dni || '',
      saldo: Math.max(0, saldo),
      montoVencido,
      ultimaCompra: ultimaCompra || cliente.ultima_compra || null,
      ultimoPago: ultimoPago || cliente.fecha_ultimo_pago || null,
      estado,
      cantidadVentasDeuda,
      diasAtraso: maxDiasAtraso,
      diasVencido: estado === CRM_ESTADOS.VENCIDO ? maxDiasAtraso : 0,
      fechaDeudaMasAntigua: fechaMasAntigua,
      // Riesgo residual: listado global puede no traer ventas (query saldo_pendiente>0).
      sinVentasEnAgregado: ventas.length === 0 && saldo === 0
    };
  });
}

/** KPIs derivados exclusivamente de filas CRM ya resueltas con resolveSaldoVenta. */
export function buildKpisCrmCuentaCorriente(filas, _deudaResumenLegacy = null, cobrosHoy = 0) {
  const conDeuda = filas.filter((f) => f.saldo > 0);
  const vencidos = filas.filter((f) => f.estado === CRM_ESTADOS.VENCIDO);
  const alDia = filas.filter((f) => f.estado === CRM_ESTADOS.AL_DIA);

  return {
    clientesConDeuda: conDeuda.length,
    totalAdeudado: conDeuda.reduce((s, f) => s + f.saldo, 0),
    clientesVencidos: vencidos.length,
    cobrosRegistradosHoy: cobrosHoy,
    clientesAlDia: alDia.length
  };
}

export function filtrarFilasCrmCuentaCorriente(filas, filtro, searchTerm) {
  let result = [...(filas || [])];

  switch (filtro) {
    case CRM_FILTROS.CON_DEUDA:
      result = result.filter((f) => f.saldo > 0);
      break;
    case CRM_FILTROS.AL_DIA:
      result = result.filter((f) => f.estado === CRM_ESTADOS.AL_DIA);
      break;
    case CRM_FILTROS.VENCIDOS:
      result = result.filter((f) => f.estado === CRM_ESTADOS.VENCIDO);
      break;
    case CRM_FILTROS.TOP_DEUDORES:
      result = result.filter((f) => f.saldo > 0);
      break;
    case CRM_FILTROS.SIN_MOVIMIENTOS:
      result = result.filter((f) => f.estado === CRM_ESTADOS.SIN_MOVIMIENTOS);
      break;
    default:
      break;
  }

  result = ordenarPorFiltro(result, filtro);

  if (filtro === CRM_FILTROS.TOP_DEUDORES) {
    result = result.slice(0, 20);
  }

  const termino = String(searchTerm || '').trim().toLowerCase();
  if (!termino) return result;

  return result.filter((f) => (
    f.nombre.toLowerCase().includes(termino)
    || String(f.codigo || '').toLowerCase().includes(termino)
    || String(f.telefono || '').includes(termino)
    || String(f.dni_cuit || '').includes(termino)
    || String(f.documento || '').includes(termino)
    || String(f.id || '').toLowerCase().includes(termino)
  ));
}

export function extraerCobrosHoyDesdeResumenPagos(resumen) {
  const raw = resumen?.data ?? resumen;
  const totalPagos = parseInt(raw?.total_pagos, 10);
  if (Number.isFinite(totalPagos) && totalPagos >= 0) return totalPagos;
  const monto = parseFloat(raw?.total_monto);
  if (Number.isFinite(monto) && monto > 0) return monto;
  return 0;
}

const ORDEN_ESTADO = {
  [CRM_ESTADOS.VENCIDO]: 0,
  [CRM_ESTADOS.CON_DEUDA]: 1,
  [CRM_ESTADOS.AL_DIA]: 2,
  [CRM_ESTADOS.SIN_MOVIMIENTOS]: 3
};

function compararAntiguedadDeuda(a, b) {
  const diasDiff = (b.diasVencido ?? b.diasAtraso ?? 0) - (a.diasVencido ?? a.diasAtraso ?? 0);
  if (diasDiff !== 0) return diasDiff;
  const saldoDiff = (b.saldo ?? 0) - (a.saldo ?? 0);
  if (saldoDiff !== 0) return saldoDiff;
  const fechaA = parseFechaMs(a.fechaDeudaMasAntigua);
  const fechaB = parseFechaMs(b.fechaDeudaMasAntigua);
  if (fechaA && fechaB && fechaA !== fechaB) return fechaA - fechaB;
  return a.nombre.localeCompare(b.nombre, 'es');
}

/** Niveles visuales CRM — solo presentación, sin alterar montos. */
export const CRM_ANTIGUEDAD = {
  CRITICO: 'critico',
  ALERTA: 'alerta',
  TEMPRANO: 'temprano',
  AL_DIA: 'al_dia',
  NEUTRO: 'neutro'
};

export function resolverAntiguedadVisualCrm(fila) {
  if (!fila || (fila.saldo ?? 0) <= 0) {
    return fila?.estado === CRM_ESTADOS.AL_DIA ? CRM_ANTIGUEDAD.AL_DIA : CRM_ANTIGUEDAD.NEUTRO;
  }
  const dias = fila.diasAtraso ?? 0;
  if (dias > 90) return CRM_ANTIGUEDAD.CRITICO;
  if (dias >= 30) return CRM_ANTIGUEDAD.ALERTA;
  return CRM_ANTIGUEDAD.TEMPRANO;
}

/** Orden operativo: vencidos → deuda → al día → sin movimientos */
export function ordenarFilasOperativo(filas) {
  return [...(filas || [])].sort((a, b) => {
    const grupoA = ORDEN_ESTADO[a.estado] ?? 99;
    const grupoB = ORDEN_ESTADO[b.estado] ?? 99;
    if (grupoA !== grupoB) return grupoA - grupoB;
    if (grupoA <= 1) return compararAntiguedadDeuda(a, b);
    return a.nombre.localeCompare(b.nombre, 'es');
  });
}

function ordenarPorFiltro(filas, filtro) {
  switch (filtro) {
    case CRM_FILTROS.TOP_DEUDORES:
      return [...filas].sort((a, b) => (b.saldo ?? 0) - (a.saldo ?? 0) || a.nombre.localeCompare(b.nombre, 'es'));
    case CRM_FILTROS.VENCIDOS:
      return [...filas].sort((a, b) => compararAntiguedadDeuda(a, b));
    case CRM_FILTROS.AL_DIA:
    case CRM_FILTROS.SIN_MOVIMIENTOS:
      return [...filas].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    case CRM_FILTROS.CON_DEUDA:
      return [...filas].sort((a, b) => compararAntiguedadDeuda(a, b));
    default:
      return ordenarFilasOperativo(filas);
  }
}
